import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Scale, LogOut, ShoppingCart, FileText, Calendar, Download,
  Phone, Mail, User, Trophy, ChevronDown, ChevronUp, X,
  Eye, MessageSquare, Euro,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { lawyerAPI } from '@/utils/api';
import { toast } from 'sonner';

const TIER_CONFIG = {
  gold: { label: 'Or', price: 150, bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-900 border-amber-400' },
  silver: { label: 'Argent', price: 70, bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', badge: 'bg-gray-200 text-gray-800 border-gray-400' },
  bronze: { label: 'Bronze', price: 15, bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', badge: 'bg-orange-100 text-orange-800 border-orange-300' },
};

const LawyerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [purchasedLeads, setPurchasedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showPurchased, setShowPurchased] = useState(false);
  const [filterTier, setFilterTier] = useState('all');
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.is_lawyer) {
      toast.error('Accès réservé aux avocats');
      navigate('/login');
      return;
    }

    // Handle purchase confirmation
    const purchase = searchParams.get('purchase');
    const sessionId = searchParams.get('session_id');
    if (purchase === 'success' && sessionId) {
      confirmPurchase(sessionId);
    }

    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [leadsRes, purchasedRes] = await Promise.all([
        lawyerAPI.getLeads(),
        lawyerAPI.getPurchasedLeads(),
      ]);
      setLeads(leadsRes.data.leads || []);
      setPurchasedLeads(purchasedRes.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const confirmPurchase = async (sessionId) => {
    try {
      const res = await lawyerAPI.confirmPurchase(sessionId);
      toast.success(`${res.data.purchased_count} lead(s) acheté(s) avec succès !`);
      setCart([]);
      fetchData();
      // Clean URL
      navigate('/lawyer', { replace: true });
    } catch (error) {
      toast.error('Erreur lors de la confirmation du paiement');
    }
  };

  const toggleCart = (lead) => {
    setCart(prev => {
      const exists = prev.find(l => l.id === lead.id);
      if (exists) return prev.filter(l => l.id !== lead.id);
      return [...prev, lead];
    });
  };

  const cartTotal = cart.reduce((sum, l) => sum + (TIER_CONFIG[l.lead_tier]?.price || 0), 0);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setPurchasing(true);
    try {
      const res = await lawyerAPI.purchaseLeads(cart.map(l => l.id));
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      if (error.response?.status === 409) {
        const unavailable = error.response.data.detail.unavailable_ids || [];
        setCart(prev => prev.filter(l => !unavailable.includes(l.id)));
        setLeads(prev => prev.filter(l => !unavailable.includes(l.id)));
        toast.error('Certains leads ne sont plus disponibles et ont été retirés du panier');
      } else {
        toast.error('Erreur lors du paiement');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleDownloadFile = async (dossierId, file) => {
    try {
      const res = await lawyerAPI.downloadFile(dossierId, file.file_id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const filteredLeads = filterTier === 'all' ? leads : leads.filter(l => l.lead_tier === filterTier);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Scale size={32} color="#1e3a8a" strokeWidth={2.5} />
              <h1 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                Espace Avocat
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Cart button */}
              <Button
                variant={cart.length > 0 ? 'default' : 'outline'}
                onClick={() => setShowCart(!showCart)}
                className="relative"
              >
                <ShoppingCart size={18} className="mr-2" />
                Panier
                {cart.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-white text-blue-900">
                    {cart.length}
                  </span>
                )}
              </Button>
              <Button variant="ghost" onClick={handleLogout}>
                <LogOut className="mr-2" size={20} />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cart panel */}
        {showCart && cart.length > 0 && (
          <div className="mb-6 p-6 bg-white rounded-lg border-2 border-blue-200 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                <ShoppingCart size={20} />
                Votre panier ({cart.length} lead{cart.length > 1 ? 's' : ''})
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCart(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="space-y-2 mb-4">
              {cart.map(lead => {
                const tier = TIER_CONFIG[lead.lead_tier];
                return (
                  <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${tier?.badge}`}>
                        {tier?.label}
                      </span>
                      <span className="text-sm text-gray-600">
                        Dossier estimé à {lead.lead_valuation?.toLocaleString('fr-FR')}€
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">{tier?.price}€</span>
                      <Button variant="ghost" size="sm" onClick={() => toggleCart(lead)}>
                        <X size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between pt-4 border-t">
              <span className="text-xl font-bold text-blue-900">Total : {cartTotal}€</span>
              <Button onClick={handleCheckout} disabled={purchasing} size="lg">
                {purchasing ? 'Redirection...' : `Payer ${cartTotal}€`}
              </Button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <p className="text-sm text-gray-500 mb-1">Leads disponibles</p>
            <p className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>{leads.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <p className="text-sm text-gray-500 mb-1">Leads achetés</p>
            <p className="text-3xl font-bold text-green-600">{purchasedLeads.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <p className="text-sm text-gray-500 mb-1">Dans le panier</p>
            <p className="text-3xl font-bold text-amber-600">{cart.length} ({cartTotal}€)</p>
          </div>
        </div>

        {/* Available Leads */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{ color: '#1e3a8a' }}>
              Leads disponibles
            </h2>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les tiers</SelectItem>
                <SelectItem value="gold">Or uniquement</SelectItem>
                <SelectItem value="silver">Argent uniquement</SelectItem>
                <SelectItem value="bronze">Bronze uniquement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredLeads.length === 0 ? (
            <div className="bg-white p-12 rounded-lg text-center border">
              <Trophy size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucun lead disponible pour le moment</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredLeads.map(lead => {
                const tier = TIER_CONFIG[lead.lead_tier];
                const inCart = cart.some(l => l.id === lead.id);
                return (
                  <div
                    key={lead.id}
                    className={`p-5 rounded-lg border-2 transition-all ${
                      inCart ? 'border-blue-400 bg-blue-50 shadow-md' : `${tier?.border} ${tier?.bg}`
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-bold border ${tier?.badge}`}>
                        {tier?.label}
                      </span>
                      <span className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                        {tier?.price}€
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-500" />
                        <span className="font-medium text-gray-800">{lead.client_display_name}</span>
                      </div>
                      {lead.lead_call_status === 'qualifie' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300 flex items-center gap-1">
                          <Phone size={11} /> Qualifié
                        </span>
                      )}
                      {lead.lead_call_status === 'injoignable' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700 border border-gray-300 flex items-center gap-1">
                          <Phone size={11} /> Injoignable
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Euro size={14} />
                        <span>Estimé à <strong>{lead.lead_valuation?.toLocaleString('fr-FR')}€</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FileText size={14} />
                        <span>{lead.file_count} document{lead.file_count > 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar size={14} />
                        <span>{formatDate(lead.created_at)}</span>
                      </div>
                      {lead.lead_admin_note && (
                        <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-md p-2 mt-1">
                          <MessageSquare size={14} className="mt-0.5 shrink-0" />
                          <span>{lead.lead_admin_note}</span>
                        </div>
                      )}
                      {lead.lead_calls?.length > 0 && (
                        <div className="space-y-1 mt-1">
                          {lead.lead_calls.map((call) => (
                            <div key={call.id} className="flex items-start gap-2 text-xs text-gray-600 bg-blue-50 rounded-md p-2">
                              <Phone size={12} className="mt-0.5 shrink-0" />
                              <span>
                                <strong>{call.status === 'qualifie' ? 'Qualifié' : 'Injoignable'}</strong>
                                {' · '}
                                {new Date(call.called_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                {call.note && <> — {call.note}</>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button
                      variant={inCart ? 'outline' : 'default'}
                      className="w-full"
                      onClick={() => toggleCart(lead)}
                    >
                      {inCart ? (
                        <>
                          <X size={16} className="mr-2" />
                          Retirer du panier
                        </>
                      ) : (
                        <>
                          <ShoppingCart size={16} className="mr-2" />
                          Ajouter au panier
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Purchased Leads */}
        <div>
          <button
            onClick={() => setShowPurchased(!showPurchased)}
            className="w-full flex items-center justify-between p-4 bg-white rounded-lg border mb-4"
          >
            <h2 className="text-xl font-bold text-green-800 flex items-center gap-2">
              <Eye size={20} />
              Mes leads achetés ({purchasedLeads.length})
            </h2>
            {showPurchased ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {showPurchased && (
            <div className="space-y-4">
              {purchasedLeads.length === 0 ? (
                <div className="bg-white p-8 rounded-lg text-center border">
                  <p className="text-gray-500">Vous n'avez pas encore acheté de leads</p>
                </div>
              ) : (
                purchasedLeads.map(lead => {
                  const tier = TIER_CONFIG[lead.lead_tier];
                  return (
                    <div key={lead.id} className="bg-white p-6 rounded-lg border shadow-sm space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${tier?.badge}`}>
                              {tier?.label}
                            </span>
                            <span className="text-sm text-gray-500">
                              Acheté le {formatDate(lead.lead_purchased_at)}
                            </span>
                          </div>

                          {/* Contact info */}
                          <div className="space-y-1 pt-2">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-semibold text-lg">{lead.nom}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail size={16} className="text-gray-500" />
                              <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">
                                {lead.email}
                              </a>
                            </div>
                            {lead.telephone && (
                              <div className="flex items-center gap-2">
                                <Phone size={16} className="text-gray-500" />
                                <a href={`tel:${lead.telephone}`} className="text-blue-600 hover:underline">
                                  {lead.telephone}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="text-sm text-gray-500">Valorisation</p>
                          <p className="text-xl font-bold text-green-700">
                            {lead.lead_valuation?.toLocaleString('fr-FR')}€
                          </p>
                        </div>
                      </div>

                      {/* Admin note */}
                      {lead.lead_admin_note && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-1">
                            <MessageSquare size={14} />
                            Note de l'analyste
                          </p>
                          <p className="text-sm text-amber-900 whitespace-pre-wrap">{lead.lead_admin_note}</p>
                        </div>
                      )}

                      {/* Documents */}
                      {lead.files && lead.files.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                            <FileText size={14} />
                            Documents ({lead.files.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {lead.files.map(file => (
                              <Button
                                key={file.file_id}
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadFile(lead.id, file)}
                                className="text-xs"
                              >
                                <Download size={14} className="mr-1" />
                                {file.filename?.length > 25
                                  ? file.filename.substring(0, 22) + '...'
                                  : file.filename}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LawyerDashboard;
